// Scroll-snap carousel controls. The track ([data-track]) scrolls natively (swipe, trackpad, keyboard); this only adds
// one dot per scroll position into [data-dots] (optional), marks the current one and scrolls to a dot's position when
// clicked. Optional [data-prev] / [data-next] buttons step one card and are marked aria-disabled at either end (a
// `disabled` attribute in the markup is cleared, so buttons can start disabled before the script runs). When
// everything fits, the element gets a [data-static] attribute so arrows can be hidden in CSS.
class Carousel extends HTMLElement {
  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.dots = this.querySelector('[data-dots]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    if (!this.track || (!this.dots && !this.prev && !this.next)) return;

    this.dots?.addEventListener('click', (event) => {
      const dot = event.target.closest('button');
      if (dot) this.#go(Number(dot.dataset.index));
    });
    this.prev?.addEventListener('click', () => this.#go(this.#index() - 1));
    this.next?.addEventListener('click', () => this.#go(this.#index() + 1));
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.#mark());
    });
    this.resizeObserver = new ResizeObserver(() => this.#build());
    this.resizeObserver.observe(this.track);
  }

  #stride() {
    const [first, second] = this.track.children;
    return second ? second.offsetLeft - first.offsetLeft : this.track.clientWidth;
  }

  #index() {
    return Math.min(this.positions - 1, Math.round(this.track.scrollLeft / this.#stride()));
  }

  #go(index) {
    const target = Math.max(0, Math.min(this.positions - 1, index));
    const instant = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: target * this.#stride(), behavior: instant ? 'auto' : 'smooth' });
  }

  #build() {
    const stride = this.#stride();
    const visible = Math.max(1, Math.round(this.track.clientWidth / stride));
    this.positions = Math.max(1, this.track.children.length - visible + 1);
    this.toggleAttribute('data-static', this.positions < 2);
    if (this.dots) {
      this.dots.hidden = this.positions < 2;
      if (this.dots.children.length !== this.positions) {
        this.dots.replaceChildren(
          ...Array.from({ length: this.positions }, (_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.dataset.index = index;
            dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${this.positions}`);
            return dot;
          })
        );
      }
    }
    this.#mark();
  }

  #mark() {
    const index = this.#index();
    if (this.dots) {
      [...this.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }
    // aria-disabled rather than disabled, so a focused arrow keeps focus at the end of the track
    for (const [button, atEnd] of [[this.prev, index <= 0], [this.next, index >= this.positions - 1]]) {
      if (!button) continue;
      button.disabled = false;
      button.setAttribute('aria-disabled', String(atEnd));
    }
  }
}

if (!customElements.get('pl-carousel')) customElements.define('pl-carousel', Carousel);
